// ADR 7.9.26k §4 — hand the link to the platform, or to the clipboard.
//
// DOM-only and `t`-free on purpose: the caller passes the already-translated
// sheet text, so this module has no opinion about copy and no reason to import
// i18n. It is the web's equivalent of the natives' share sheet, and it is two
// APIs rather than one because `navigator.share` is absent on every desktop
// browser that is not Safari.
//
// Safari's transient activation can expire across an awaited mint — idle → mint
// → share is one click, but the network sits in the middle of it — so a share
// that was legal when the user clicked can reject by the time it is called. The
// answer is the copy fallback plus the "copied" flash, not a retry: a retry
// would need a second click anyway, and asking for one is worse than quietly
// putting the URL where the user can paste it.

export type ShareOutcome = 'shared' | 'copied' | 'failed'

function isTransientActivationFailure(error: unknown): boolean {
  return error instanceof Error && error.name === 'NotAllowedError'
}

/**
 * The clipboard half on its own — never `'shared'`. Exported for ADR 23.9.26p
 * §4, whose reveal panel copies an API token with it.
 */
export async function copy(url: string): Promise<ShareOutcome> {
  try {
    await navigator.clipboard.writeText(url)
    return 'copied'
  } catch {
    return 'failed'
  }
}

/** Share `url` with `text`, falling back to the clipboard. */
export async function shareOrCopy(url: string, text: string): Promise<ShareOutcome> {
  if (typeof navigator.share !== 'function') return await copy(url)
  try {
    await navigator.share({ text, url })
    return 'shared'
  } catch (error) {
    // A dismissed sheet is not a failure to route around — the user said no.
    // Only the transient-activation rejection falls through to the clipboard.
    return isTransientActivationFailure(error) ? await copy(url) : 'failed'
  }
}
